/**
 * Decision 205 — the monthly board pack: pure helpers shared by the server
 * (figures, the PowerPoint) and the page (the same figures, live).
 *
 * The slide's arithmetic in one place, so the page, the export and the tests
 * cannot drift: a section's whole-population total, its highlighted-rows
 * total, the family roll-up and the "£9k tcv (3yr)" cell wording.
 *
 * Decision 245 (Mark, 2026-09-19): the stage-weighted pipeline is GONE — the
 * one weighted forecast is the Wave9 Hub's ARR Cockpit. Pipeline rows still
 * show their HubSpot stage; nothing here multiplies by a probability.
 */

/**
 * Decision 262 (Mark, 2026-10-08): the booked section is GONE from the picks — the
 * bookings slide (decision 254) carries the month's orders in full. Two sections
 * remain, each with a pick target the page asks for: pipeline 10, notable 5.
 */
export type BoardSection = 'pipeline' | 'notable'
export const BOARD_SECTIONS: BoardSection[] = ['pipeline', 'notable']
export const SECTION_TARGETS: Record<BoardSection, number> = { pipeline: 10, notable: 5 }

export const SECTION_TITLES: Record<BoardSection, string> = {
  pipeline: 'Pipeline / favourable (commit) opportunities',
  notable: 'Notable new opportunities',
}

export type BoardRowKind = 'quote' | 'booking' | 'manual'

/** One resolved row of a pack — system figures live, hand-typed fields stored. */
export interface BoardRow {
  id: string
  section: BoardSection
  kind: BoardRowKind
  sort: number
  /** Quote id / historic booking id, by kind. */
  quoteId: string | null
  historicId: string | null
  quoteNumber: string | null
  /** The "Type" column: customer (falls back to the quote title / deal name). */
  name: string
  /** The "Details" column: family roll-up, or the manual row's own family. */
  family: string | null
  /** Product Lines that had no family — the page names them so an admin can map them. */
  unmappedLines: string[]
  tcv: number
  oneOff: number
  arr: number
  termMonths: number | null
  /** Open rows: HubSpot stage. */
  stageLabel: string | null
  status: string | null
  ownerName: string | null
  acceptedAt: string | null
  sentAt: string | null
  /** Sites: stored override, else derived from quote_sites (null for bookings / manual). */
  sites: number | null
  sitesDerived: number | null
  liveBy: string | null
  notes: string | null
}

export interface SectionPopulation {
  /** What the population IS, in words — the slide's footer says it. */
  label: string
  n: number
  tcv: number
  sites: number | null
}

export interface SectionTotals {
  highlightedN: number
  highlightedTcv: number
  highlightedSites: number
}

export interface BoardPackView {
  id: string
  month: string                 // YYYY-MM
  monthLabel: string            // "August 2026"
  rows: BoardRow[]
  population: Record<BoardSection, SectionPopulation>
  totals: Record<BoardSection, SectionTotals>
  summary: BoardSummary
  /** Decision 254: the new-business bookings slide — the month's list + 13 months of history. */
  bookings: BookingsView
  /** Decision 261: the WHOLE open new-business pipeline (the population behind the Pipeline tile), biggest first, with the board's picks flagged. */
  pipeline?: PipelineView
  updatedAt: string | null
  updatedBy: string | null
}

// ---------------------------------------------------------------- pipeline (decision 261)

/** One open new-business quote — the same figures a BoardRow carries, plus whether the board picked it. */
export interface PipelineQuote {
  id: string
  quoteNumber: string | null
  name: string
  title: string | null
  family: string | null
  partnerName: string | null
  ownerName: string | null
  tcv: number
  oneOff: number
  arr: number
  termMonths: number | null
  stageLabel: string | null
  status: string | null
  sentAt: string | null
  sites: number | null
  /** The pack section that picked this quote (pipeline | notable), or null when it is population only. */
  picked: 'pipeline' | 'notable' | null
  /** The pick's sort, live-by and notes, when picked. */
  pickSort: number | null
  liveBy: string | null
  notes: string | null
}

export interface PipelineView {
  /** What the population is, in words. */
  label: string
  n: number
  tcv: number
  rows: PipelineQuote[]
}

// ---------------------------------------------------------------- bookings (decision 254)

/** One new-business booking in the pack's month — a portal acceptance or a #202 HubSpot booking. */
export interface BookingRow {
  kind: 'quote' | 'booking'
  id: string
  quoteNumber: string | null
  /** Customer (falls back to the quote title / deal name). */
  name: string
  title: string | null
  family: string | null
  partnerName: string | null
  tcv: number
  oneOff: number
  arr: number
  termMonths: number | null
  acceptedAt: string
  sites: number | null
}

/** One month of the rolling history. */
export interface BookingMonth {
  month: string          // YYYY-MM
  label: string          // "Sep 25"
  n: number
  tcv: number
  oneOff: number
  arr: number
  /** False for a month before the earliest booking on record (portal or backfill) — shown as no data, not £0. */
  covered: boolean
  /** Decision 255: the month's figures are a hand-entered summary (`board_booking_months`), not computed from records. */
  manual: boolean
}

export interface BookingsTotals {
  n: number
  tcv: number
  oneOff: number
  arr: number
}

export interface BookingsView {
  /** What the population is, in words. */
  label: string
  rows: BookingRow[]
  totals: BookingsTotals
  /** Oldest first, HISTORY_MONTHS long, ending in the pack's month. */
  history: BookingMonth[]
  /** The earliest month with a booking on record, or null when there is none. */
  coveredFrom: string | null
  /** Decision 256: the 12 months ending in the pack's month — total and the average month. */
  rolling: RollingSummary
}

export interface RollingSummary {
  months: number          // the window, 12
  covered: number         // months in it with data (records or a hand-entered summary)
  total: BookingsTotals
  /** Per covered month — the same as ÷ 12 when every month is covered. */
  average: BookingsTotals
}

export const ROLLING_MONTHS = 12

/** The slide shows the pack's month and the twelve before it. */
export const HISTORY_MONTHS = 13

export interface BoardSummary {
  pipelineTcv: number
  notableTcv: number
  combinedTcv: number
  pipelineSites: number
  notableSites: number
  combinedSites: number
  /** The pipeline picks by TCV, biggest first (the slide's top 3). */
  topPipeline: Array<{ name: string; tcv: number }>
  /** Share of the combined TCV per section, for the donut. */
  split: Array<{ section: BoardSection; label: string; tcv: number; share: number }>
}

// ---------------------------------------------------------------- months

/** "2026-08" → { start: 2026-08-01, end: 2026-09-01 } (UTC). */
export function monthBounds(month: string): { start: Date; end: Date } {
  const m = /^(\d{4})-(\d{2})$/.exec(month)
  if (!m) throw new Error(`Not a month: ${month}`)
  const y = Number(m[1]), mo = Number(m[2]) - 1
  if (mo < 0 || mo > 11) throw new Error(`Not a month: ${month}`)
  return { start: new Date(Date.UTC(y, mo, 1)), end: new Date(Date.UTC(y, mo + 1, 1)) }
}

export function monthLabel(month: string): string {
  const { start } = monthBounds(month)
  return start.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

/** The month before "2026-08" → "2026-07". */
export function previousMonth(month: string): string {
  const { start } = monthBounds(month)
  const p = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() - 1, 1))
  return `${p.getUTCFullYear()}-${String(p.getUTCMonth() + 1).padStart(2, '0')}`
}

export function currentMonth(now = new Date()): string {
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`
}

/** The n months ending in `month`, oldest first: historyMonths('2026-09', 3) → ['2026-07', '2026-08', '2026-09']. */
export function historyMonths(month: string, n = HISTORY_MONTHS): string[] {
  const { start } = monthBounds(month)
  const out: string[] = []
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() - i, 1))
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`)
  }
  return out
}

/** "2026-09" → "Sep 26" — the chart's axis label. */
export function monthShortLabel(month: string): string {
  const { start } = monthBounds(month)
  // A fixed list — ICU's en-GB says "Sept", and the slide and the page must agree.
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${MONTHS[start.getUTCMonth()]} ${String(start.getUTCFullYear()).slice(2)}`
}

/** Totals over a month's bookings — the list's footer and the tile. */
export function bookingsTotals(rows: BookingRow[]): BookingsTotals {
  return {
    n: rows.length,
    tcv: round2(rows.reduce((s, r) => s + r.tcv, 0)),
    oneOff: round2(rows.reduce((s, r) => s + r.oneOff, 0)),
    arr: round2(rows.reduce((s, r) => s + r.arr, 0)),
  }
}

/** Decision 256: the last `months` buckets of the history totalled, and the average per covered month. */
export function rollingSummary(history: BookingMonth[], months = ROLLING_MONTHS): RollingSummary {
  const window = history.slice(-months)
  const covered = window.filter((m) => m.covered)
  const total = {
    n: covered.reduce((s, m) => s + m.n, 0),
    tcv: round2(covered.reduce((s, m) => s + m.tcv, 0)),
    oneOff: round2(covered.reduce((s, m) => s + m.oneOff, 0)),
    arr: round2(covered.reduce((s, m) => s + m.arr, 0)),
  }
  const d = Math.max(1, covered.length)
  return {
    months, covered: covered.length, total,
    average: { n: Math.round(total.n / d * 10) / 10, tcv: round2(total.tcv / d), oneOff: round2(total.oneOff / d), arr: round2(total.arr / d) },
  }
}

/**
 * The rolling history from per-month sums: every month in the window, in
 * order, with months before the earliest booking on record marked uncovered
 * (the #202 backfill starts on a date — before it, "no data" is the truth, £0 is not).
 */
export function bookingHistory(
  month: string,
  sums: Map<string, { n: number; tcv: number; oneOff: number; arr: number }>,
  coveredFrom: string | null,
  n = HISTORY_MONTHS,
  manual: Map<string, { n: number; tcv: number; oneOff: number; arr: number }> = new Map(),
): BookingMonth[] {
  return historyMonths(month, n).map((m) => {
    // Decision 255: a hand-entered month REPLACES the computed one and counts as covered.
    const hand = manual.get(m)
    const s = hand ?? sums.get(m)
    return {
      month: m, label: monthShortLabel(m),
      n: s?.n ?? 0, tcv: round2(s?.tcv ?? 0), oneOff: round2(s?.oneOff ?? 0), arr: round2(s?.arr ?? 0),
      covered: hand != null || (coveredFrom != null && m >= coveredFrom),
      manual: hand != null,
    }
  })
}

// ---------------------------------------------------------------- figures

export function sectionTotals(rows: BoardRow[], section: BoardSection): SectionTotals {
  const rs = rows.filter((r) => r.section === section)
  return {
    highlightedN: rs.length,
    highlightedTcv: round2(rs.reduce((s, r) => s + r.tcv, 0)),
    highlightedSites: rs.reduce((s, r) => s + (r.sites ?? r.sitesDerived ?? 0), 0),
  }
}

/**
 * The summary column. Section totals are the POPULATIONS (the whole month's
 * bookings, every open quote, everything sent this month) — the tables show
 * the highlights, the callouts show the company (the #203 rule: a share is of
 * the whole population, never of the rows shown). Sites come from the
 * highlighted rows because only they carry a typed site count.
 */
export function packSummary(
  population: Record<BoardSection, SectionPopulation>,
  totals: Record<BoardSection, SectionTotals>,
  rows: BoardRow[],
): BoardSummary {
  const pipelineTcv = population.pipeline.tcv
  const notableTcv = population.notable.tcv
  const combinedTcv = round2(pipelineTcv + notableTcv)
  const share = (v: number) => (combinedTcv > 0 ? v / combinedTcv : 0)
  const topPipeline = rows
    .filter((r) => r.section === 'pipeline')
    .sort((a, b) => b.tcv - a.tcv)
    .slice(0, 3)
    .map((r) => ({ name: r.name, tcv: r.tcv }))
  return {
    pipelineTcv, notableTcv, combinedTcv,
    pipelineSites: totals.pipeline.highlightedSites,
    notableSites: totals.notable.highlightedSites,
    combinedSites: totals.pipeline.highlightedSites + totals.notable.highlightedSites,
    topPipeline,
    split: [
      { section: 'pipeline', label: 'Pipeline', tcv: pipelineTcv, share: share(pipelineTcv) },
      { section: 'notable', label: 'Notable new', tcv: notableTcv, share: share(notableTcv) },
    ],
  }
}

// ---------------------------------------------------------------- families

/**
 * A quote's "Details" family: the distinct families of its line items'
 * Product Lines, in first-seen order, joined with " + ". Lines whose Product
 * Line has no family are reported separately so the page can say what to map.
 */
export function familyRollup(
  lineKeys: string[],
  familyByLine: Map<string, string | null | undefined>,
): { family: string | null; unmapped: string[] } {
  const fams: string[] = []
  const unmapped: string[] = []
  for (const key of lineKeys) {
    const f = familyByLine.get(key)?.trim()
    if (f) { if (!fams.includes(f)) fams.push(f) }
    else if (key && !unmapped.includes(key)) unmapped.push(key)
  }
  return { family: fams.length ? fams.join(' + ') : null, unmapped }
}

// ---------------------------------------------------------------- wording

/** "£9k tcv (3yr)" — the slide's TCV cell. £ compact to one decimal under £10k. */
export function tcvCell(tcv: number, termMonths: number | null): string {
  const term = termMonths && termMonths > 0 ? ` (${termMonths % 12 === 0 ? `${termMonths / 12}yr` : `${termMonths}m`})` : ''
  return `${compactGBP(tcv)} tcv${term}`
}

/** £91.5k · £550k · £2.6m — the slide's compact money. */
export function compactGBP(n: number): string {
  const abs = Math.abs(n)
  const sign = n < 0 ? '-' : ''
  if (abs >= 1_000_000) return `${sign}£${trimZero((abs / 1_000_000).toFixed(abs >= 10_000_000 ? 1 : 2))}m`
  if (abs >= 1_000) return `${sign}£${trimZero((abs / 1_000).toFixed(abs >= 100_000 ? 0 : 1))}k`
  return `${sign}£${trimZero(abs.toFixed(0))}`
}

function trimZero(s: string): string {
  return s.includes('.') ? s.replace(/\.?0+$/, '') : s
}

export function round2(n: number): number {
  return Math.round(n * 100) / 100
}
